import { useCallback } from "react";
import { useBuilderStore } from "../_stores/builder-store";
import { usePublicViewContext } from "../_components/public/PublicViewContext";

/**
 * Hook for managing inline editing state on the canvas.
 * Provides helpers to start/stop inline editing and check if a specific field is active.
 *
 * @param componentId - The component that owns the editable field
 */
export function useInlineEdit(componentId: string): {
  startEditing: (fieldId: string) => void;
  stopEditing: () => void;
  isEditing: (fieldId: string) => boolean;
  updateField: (field: string, value: string) => void;
  commitEdit: (field: string, value: string) => void;
  isPublic: boolean;
} {
  const { isPublic } = usePublicViewContext();
  const inlineEditingField = useBuilderStore((s) => s.inlineEditingField);
  const setInlineEditingField = useBuilderStore((s) => s.setInlineEditingField);
  const setSelectedId = useBuilderStore((s) => s.setSelectedId);
  const updateComponent = useBuilderStore((s) => s.updateComponent);
  const requestSave = useBuilderStore((s) => s.requestSave);

  /** Start inline editing a specific field */
  const startEditing = useCallback(
    (fieldId: string) => {
      if (isPublic) return;
      setSelectedId(componentId);
      setInlineEditingField({ componentId, fieldId });
    },
    [isPublic, componentId, setSelectedId, setInlineEditingField]
  );

  /** Stop inline editing and request a save */
  const stopEditing = useCallback(() => {
    setInlineEditingField(null);
    requestSave();
  }, [setInlineEditingField, requestSave]);

  /** Check if a specific field on this component is currently being inline-edited */
  const isEditing = useCallback(
    (fieldId: string) => inlineEditingField?.componentId === componentId && inlineEditingField?.fieldId === fieldId,
    [inlineEditingField, componentId]
  );

  /** Update a field value without stopping editing (for real-time sync during typing) */
  const updateField = useCallback(
    (field: string, value: string) => {
      updateComponent(componentId, { [field]: value } as never);
    },
    [componentId, updateComponent]
  );

  /** Commit a value update, stop editing, and request a save */
  const commitEdit = useCallback(
    (field: string, value: string) => {
      updateComponent(componentId, { [field]: value } as never);
      setInlineEditingField(null);
      requestSave();
    },
    [componentId, updateComponent, setInlineEditingField, requestSave]
  );

  return { startEditing, stopEditing, isEditing, updateField, commitEdit, isPublic };
}
